// Copyright (c) StrangeBee 2023

import useLocalStorage, { LocalStorageKeys } from '@Hooks/useLocalStorage';
import {
    AvailableLanguages,
    LanguageDirection,
    LanguageSwitcherContext,
    StorageLanguageType,
} from '@Lang/LanguageSwitcherContext';
import { THDropdown } from '@Molecules/Inputs/THDropdown/THDropdown';
import { THMenu } from '@Molecules/THMenu/THMenu';
import React, { ReactElement, useContext } from 'react';

import './LanguageSwitcher.scss';

const languageParams: Record<AvailableLanguages, { image: string; label: string; direction?: LanguageDirection }> = {
    de: { image: 'static/flags/germany.png', label: 'Deutsch' },
    nl: { image: 'static/flags/dutch.png', label: 'Dutch' },
    en: { image: 'static/flags/uk.png', label: 'English (UK)' },
    en_US: { image: 'static/flags/us.png', label: 'English (US)' },
    es: { image: 'static/flags/spain.png', label: 'Español' },
    fr: { image: 'static/flags/france.png', label: 'Français' },
    it: { image: 'static/flags/italy.png', label: 'Italiano' },
    pl: { image: 'static/flags/poland.png', label: 'Polski' },
    pt_BR: { image: 'static/flags/portugues.png', label: 'Português' },
    ru: { image: 'static/flags/russia.png', label: 'Русский' },
    sv: { image: 'static/flags/sweden.png', label: 'Svenska' },
    zh_Hans: { image: 'static/flags/china.png', label: '简体中文' },
    ja: { image: 'static/flags/japan.png', label: '日本語' },
    ar: { image: 'static/flags/arab.png', label: 'العربية', direction: 'rtl' },
};

export const LanguageSwitcher = (): ReactElement => {
    const { language, setLanguage, direction, setDirection } = useContext(LanguageSwitcherContext);

    const { storage, setStorage } = useLocalStorage<StorageLanguageType>({
        key: LocalStorageKeys.LANGUAGE_SWITCHER,
        initialValue: { language, direction },
    });
    const keyLanguage = storage.language || 'en';
    return (
        <THDropdown
            triggerElem={() => (
                <div className="language-switcher__trigger">
                    <img
                        className="language-switcher__image"
                        src={languageParams[keyLanguage].image}
                        alt={languageParams[keyLanguage].label}
                    />
                    <div>{languageParams[keyLanguage].label}</div>
                </div>
            )}
            trigger={['click']}
            placement="bottom"
            dropdownClassName="language-switcher__test"
        >
            {Object.entries(languageParams).map(([key]) => (
                <THMenu.Item
                    key={'settings' + key}
                    onClick={() => {
                        const keyParsed = key as AvailableLanguages;
                        setLanguage(keyParsed);
                        setDirection(languageParams[key as AvailableLanguages].direction || 'ltr');
                        setStorage({
                            language: keyParsed,
                            direction: languageParams[key as AvailableLanguages].direction || 'ltr',
                        });
                    }}
                    className="language-switcher__option"
                >
                    <span>
                        <img
                            className="language-switcher__image"
                            src={languageParams[key as AvailableLanguages].image}
                            alt={languageParams[key as AvailableLanguages].label}
                        />
                    </span>
                    <span>{languageParams[key as AvailableLanguages].label}</span>
                </THMenu.Item>
            ))}
        </THDropdown>
    );
};
